import { Typography } from "@mui/material";
import { makeStyles, useTheme } from "@/compat/muiStyles";
import { differenceInCalendarDays } from "date-fns";
import CardBase from "../../../components/cards/CardBase";
import useRiskAssessmentHistory from "../../hooks/queries/useRiskAssessmentHistory";
import useRiskAssessmentHistoryAcknowledgementRecipients from "../../hooks/queries/useRiskAssessmentHistoryAcknowledgementRecipients";
import { actionsStatus } from "../../constants/actionStatusConstants";

const ACKNOWLEDGED = 2;

const useStyles = makeStyles((theme) => ({
    row: {
        display: "flex",
        flexWrap: "wrap",
    },
    cell: {
        flex: "1 1 130px",
        minWidth: 110,
        textAlign: "center",
        padding: theme.spacing(0.5, 2),
        borderRight: `1px solid ${theme.palette.divider}`,
        "&:last-child": { borderRight: "none" },
    },
    figure: {
        fontWeight: 700,
        lineHeight: 1.1,
    },
    denominator: {
        color: theme.palette.text.disabled,
        fontWeight: 700,
    },
    label: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
}));

// Latest-occurrence summary metrics, computed client-side (no metrics endpoint).
export default function RiskAssessmentMetricsBar({ riskAssessmentHistoryId, dueDate }) {
    const classes = useStyles();
    const theme = useTheme();
    const { data, isLoading, isFetching, error } = useRiskAssessmentHistory(riskAssessmentHistoryId);
    const recipientsQuery = useRiskAssessmentHistoryAcknowledgementRecipients(riskAssessmentHistoryId);

    const hazards = Array.isArray(data?.riskAssessmentHazards) ? data.riskAssessmentHazards : [];
    const actions = Array.isArray(data?.actions) ? data.actions : [];
    const recipients = Array.isArray(recipientsQuery.data) ? recipientsQuery.data : [];

    const actionsCompleted = actions.filter((a) => a.status === actionsStatus.COMPLETED).length;
    const allActionsComplete = actions.length > 0 && actionsCompleted === actions.length;
    const acknowledged = recipients.filter((r) => r.status === ACKNOWLEDGED).length;

    // In place = assessed-in-place + completed actions (display only; InPlace untouched).
    const controls = hazards.flatMap((h) => [...(h.hazardControlsValues ?? []), ...(h.customHazardControls ?? [])]);
    const controlsAssessedInPlace = controls.filter((c) => c.inPlace === true).length;
    const controlsInPlace = Math.min(controls.length, controlsAssessedInPlace + actionsCompleted);

    // Highest residual (post-control) risk across hazards; thresholds mirror
    // QuestionMapper (>14 High, >7 Medium, >0 Low).
    const highestScore = hazards.reduce(
        (max, h) => Math.max(max, (h.severityScorePostControl ?? 0) * (h.likelihoodScorePostControl ?? 0)),
        0
    );
    const riskBand =
        highestScore > 14
            ? { label: "High", color: theme.palette.error.main }
            : highestScore > 7
              ? { label: "Medium", color: theme.palette.warning.main }
              : highestScore > 0
                ? { label: "Low", color: theme.palette.success.main }
                : { label: "None", color: theme.palette.text.disabled };

    const daysToReview = dueDate ? differenceInCalendarDays(new Date(dueDate), new Date()) : null;
    const nextReview =
        daysToReview === null
            ? { text: "N/A", color: theme.palette.text.primary }
            : daysToReview < 0
              ? { text: "Overdue", color: theme.palette.error.main }
              : daysToReview === 0
                ? { text: "Today", color: theme.palette.text.primary }
                : { text: `${daysToReview} days`, color: theme.palette.text.primary };

    const fraction = (num, den, color) => (
        <>
            <span style={{ color }}>{num}</span>
            <span className={classes.denominator}>/{den}</span>
        </>
    );

    const cells = [
        { label: "Hazards", node: <span style={{ color: theme.palette.text.primary }}>{hazards.length}</span> },
        { label: "Controls in place", node: fraction(controlsInPlace, controls.length, theme.palette.success.main) },
        { label: "Actions", node: fraction(actionsCompleted, actions.length, allActionsComplete ? theme.palette.success.main : theme.palette.text.primary) },
        { label: "People acknowledged", node: fraction(acknowledged, recipients.length, theme.palette.secondary.main) },
        { label: "Highest risk", node: <span style={{ color: riskBand.color }}>{riskBand.label}</span> },
        { label: "Next review", node: <span style={{ color: nextReview.color }}>{nextReview.text}</span> },
    ];

    return (
        <CardBase isLoading={Boolean(riskAssessmentHistoryId) && isLoading} isFetching={Boolean(riskAssessmentHistoryId) && isFetching} error={error}>
            <div className={classes.row}>
                {cells.map((cell) => (
                    <div className={classes.cell} key={cell.label}>
                        <Typography variant="h5" component="div" className={classes.figure}>
                            {cell.node}
                        </Typography>
                        <Typography variant="caption" component="div" className={classes.label}>
                            {cell.label}
                        </Typography>
                    </div>
                ))}
            </div>
        </CardBase>
    );
}
